import { RefObject, useEffect } from "react";
import z from "zod";

import { useEditingQuestion, useSetters, useSurveyName } from "../pages/SurveyBuilder/store/store";

import useSearchQueryParams from "../../../../../hooks/useSearchQueryParams";
import { useSurvey } from "./useSurvey";

import { TformRef } from "../SurveyDetails";

export const searchParamsSchema = z.object({
	tab: z.enum(["build", "results", "preview", "participants"]).optional(),
});
/**
 * This hook is used to compare the draft and saved survey
 * The draft is found in the store (orderedQuestions filters any deleted questions)
 * Saved questions are found in the useGetSurvey hook
 * It should set the hasChanges state to true if there are any differences
 */
export const useCompareDraftToSaved = ({
	surveyId,
	formRef,
}: {
	surveyId: string;
	formRef: RefObject<TformRef | null>;
}) => {
	const { queryParams: searchParams } = useSearchQueryParams({
		schema: searchParamsSchema,
	});
	const tab = searchParams.tab;
	const surveyName = useSurveyName();
	const editingQuestion = useEditingQuestion();
	const setters = useSetters();

	const { survey, isLoading: isLoadingSurvey } = useSurvey({ surveyId });

	useEffect(() => {
		let hasChanges = false;
		const filteredSurveyQuestions = survey?.questions.filter((question) => !question.deleted) || [];

		const formValues = formRef.current?.getFormValues()?.questions;

		// only compare if the tab is build and not loading
		if (tab === "build" && !isLoadingSurvey && formValues) {
			if (survey?.name !== surveyName) {
				// survey name changed
				hasChanges = true;
			} else if (editingQuestion?._id) {
				hasChanges = true;
			} else if (filteredSurveyQuestions.length !== formValues.length) {
				hasChanges = true;
			} else {
				// if we are this far, more detailed comparison is needed
				const orderedQuestionIDs = formValues.map((q) => q._id);
				for (const questionId of orderedQuestionIDs) {
					if (!filteredSurveyQuestions.find((question) => question._id === questionId)) {
						hasChanges = true; // question was added
						break;
					}
					for (const question of filteredSurveyQuestions || []) {
						if (!orderedQuestionIDs.includes(question._id)) {
							hasChanges = true; // question was deleted
							break;
						}
						if (
							JSON.stringify(question) !== JSON.stringify(formValues.find((q) => q._id === question._id))
						) {
							hasChanges = true; // question was updated
							break;
						}
					}
				}
			}
		}
		setters.setHasChanges(hasChanges);
	}, [formRef, surveyName, survey, isLoadingSurvey, editingQuestion]);
};
